<template>
  <div v-if="showAlert" class="global-alert-container">
    <v-alert
      :type="alertType"
      prominent
      dense
      dismissible
      class="global-alert mb-0"
      @input="onDismiss"
    >
      <div class="d-flex align-center alert-layout">
        <div class="alert-message">{{ alertMessage }}</div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="actionLink"
          small
          color="white"
          outlined
          class="ml-3 alert-action"
          :to="actionLink"
        >
          {{ actionText }}
        </v-btn>
      </div>
    </v-alert>
  </div>
</template>

<script>
import {accountUsage} from "@shared/utils/accountUsage";
export default {
  name: "GlobalAlert",
  props: {
    subscription: {
      type: Object,
      default: null
    },
    creditBalance: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dismissed: false
    };
  },
  computed: {
    usage(){return accountUsage(this.$store,this.$config);},
    // $config.NO_PLAN_ALERT = {text, action} for members of a paid-only site.
    noPlanAlert() {
      var value = this.$config && this.$config.NO_PLAN_ALERT;
      return this.$store.getters.forcePaidPlans && value && value.text ? value : null;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    isFreePlan() {
      return (
        this.subscription && this.subscription.subscription_plan === "free"
      );
    },
    isPaidPlan() {
      return (
        this.subscription && this.subscription.subscription_plan !== "free"
      );
    },
    // The out-of-credits threshold is delivered on the credit-balance payload
    // (min_required, backend env MIN_CREDIT_BALANCE_TO_ACT, which defaults to
    // the price of one run) - the same value the paywall uses. Using it
    // here keeps this banner from nagging a free user who can still afford a run.
    minRequired() {
      if (
        this.creditBalance &&
        this.creditBalance.min_required !== undefined &&
        this.creditBalance.min_required !== null
      ) {
        return Number(this.creditBalance.min_required);
      }
      return 0.5;
    },
    hasNoCredits() {
      return this.creditBalance && this.creditBalance.balance <= 0;
    },
    hasLowCredits() {
      return (
        this.creditBalance &&
        this.creditBalance.balance > 0 &&
        this.creditBalance.balance < this.minRequired
      );
    },
    hasInsufficientCredits() {
      return (
        this.creditBalance && this.creditBalance.balance < this.minRequired
      );
    },
    showAlert() {
      if (this.dismissed) return false;
      if (this.usage) return !!this.usage.warning;

      // Anonymous trial guests: 0 credits is their NORMAL state (their free
      // conversion is a trial, not a credit grant). A permanent warning
      // banner is exactly the friction the guest funnel removes - the
      // subscribe dialog appears at the right moments instead.
      if (this.currentUser && this.currentUser.is_anonymous) return false;

      // Never on the main product page: that page has its own conversion
      // funnel - the in-chat subscribe card, which deliberately reveals only
      // AFTER the user sends their next message (so they have an unfinished
      // task in mind). This banner firing first would tip them off early and
      // undercut that moment, while also not even offering credit packs.
      if (this.$route && this.$route.path === "/") return false;

      // Don't show alert if we don't have subscription or credit data yet
      if (!this.subscription || !this.creditBalance) return false;

      // Check if user is on free plan with insufficient credits. Apps that
      // sell at the right moments themselves (in-page paywall, result-page
      // offer) opt out of this site-wide nag with
      // config.CREDIT_ALERT_FREE_ENABLED = false; paid-plan warnings below
      // are unaffected.
      if (this.isFreePlan && this.hasInsufficientCredits) {
        return !(this.$config && this.$config.CREDIT_ALERT_FREE_ENABLED === false);
      }

      // Check if user is on paid plan with insufficient credits
      if (this.isPaidPlan && this.hasInsufficientCredits) {
        return true;
      }

      return false;
    },
    alertType() {
      if(this.usage)return this.usage.warning ? this.usage.warning.type : 'info';
      if (this.isFreePlan) {
        return "warning";
      }

      if (this.hasNoCredits) {
        return "error";
      }

      return "info";
    },
    alertMessage() {
      if(this.usage)return this.usage.warning ? this.usage.warning.text : '';
      // Free plan with no credits. Apps without starter credits (e.g. a
      // single free trial conversion instead) override the copy via config.
      if (this.isFreePlan && this.hasNoCredits) {
        // A paid-only site has no starter credits: it asks for a plan.
        if (this.noPlanAlert) return this.noPlanAlert.text;
        return (
          (this.$config && this.$config.CREDIT_ALERT_FREE_EMPTY) ||
          "You've used your one-time starter credits. Subscribe to keep going, or add a credit pack."
        );
      }

      // Free plan with low credits (but not zero)
      if (this.isFreePlan && this.hasLowCredits) {
        return `You have only ${this.formatCredits(
          this.creditBalance.balance
        )} credits left - not enough for another run. Subscribe to keep going.`;
      }

      // Paid plan with no credits
      if (this.isPaidPlan && this.hasNoCredits) {
        return (this.$config && this.$config.CREDIT_ALERT_PAID_EMPTY) || "You've used all your credits. Buy more credits or upgrade your plan to keep going.";
      }

      // Paid plan with low credits
      if (this.isPaidPlan && this.hasLowCredits) {
        if (this.$config && this.$config.CREDIT_ALERT_PAID_LOW) return this.$config.CREDIT_ALERT_PAID_LOW;
        return `You have only ${this.formatCredits(
          this.creditBalance.balance
        )} credits remaining. Consider purchasing more credits soon.`;
      }

      return "";
    },
    actionLink() {
      if(this.usage)return this.usage.warning ? this.usage.warning.to : '';
      // Free plan with insufficient credits - the dedicated sales-funnel page
      // (/subscribe), NOT the account-management /subscription page.
      if (this.isFreePlan && this.hasInsufficientCredits) {
        return "/subscribe";
      }

      // Paid plan with insufficient credits - link to billing page
      if (this.isPaidPlan && this.hasInsufficientCredits) {
        return this.$config && this.$config.TOPUPS_ENABLED === false ? "/pricing" : "/billing";
      }

      return null;
    },
    actionText() {
      if(this.usage)return this.usage.warning ? this.usage.warning.action : '';
      // Free plan with insufficient credits
      if (this.isFreePlan && this.hasInsufficientCredits) {
        return (this.noPlanAlert && this.noPlanAlert.action) || "Upgrade Now";
      }

      // Paid plan with insufficient credits
      if (this.isPaidPlan && this.hasInsufficientCredits) {
        return this.$config && this.$config.TOPUPS_ENABLED === false ? "Compare Plans" : "Purchase Credits";
      }

      return "";
    }
  },
  methods: {
    onDismiss() {
      this.dismissed = true;
      // Reset dismissed after 1 hour
      setTimeout(() => {
        this.dismissed = false;
      }, 3600000); // 1 hour in milliseconds
    },
    formatCredits(value) {
      if (value === null || value === undefined) return "0";
      return parseFloat(Number(value).toFixed(2));
    }
  }
};
</script>

<style scoped>
.global-alert-container {
  position: sticky;
  top: 48px; /* Height of the toolbar */
  z-index: 100;
  width: 100%;
}

.global-alert {
  border-radius: 0;
}
.alert-message { min-width: 0; }
@media (max-width: 600px) {
  .alert-layout { flex-wrap: wrap; gap: 10px; }
  .alert-message { flex-basis: 100%; }
  .alert-action { margin-left: 0 !important; }
}
</style>
